import React, { useState, useEffect } from 'react';
import { crmService } from '../../services/crmService';
import { useIsHQ } from '../../hooks/useIsHQ';
import AwardsWidget from './AwardsWidget';

const STAGE_COLORS = {
  identify: '#6B7280', qualify: '#3B82F6', develop: '#8B5CF6',
  capture: '#F59E0B', propose: '#EA580C', negotiate: '#EF4444',
};

const formatM = (v) => {
  if (!v) return '$0';
  if (v >= 1e9) return `$${(v / 1e9).toFixed(1)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v}`;
};

const card = { background: '#111827', border: '1px solid #1E2D45', borderRadius: 8, padding: 16 };
const cardTitle = { margin: '0 0 12px', color: '#94A3B8', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' };

const StatCard = ({ label, value, sub, accent = '#22D3EE', onClick }) => {
  const [hovered, setHovered] = React.useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...card,
        padding: '16px 20px',
        borderTop: `2px solid ${accent}`,
        cursor: onClick ? 'pointer' : 'default',
        backgroundColor: hovered && onClick ? '#1a2332' : '#111827',
        borderColor: hovered && onClick ? accent : '#1E2D45',
        transition: 'all 0.15s',
        transform: hovered && onClick ? 'translateY(-2px)' : 'translateY(0)',
      }}
    >
      <div style={{ color: '#6B7280', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>{label}</div>
      <div style={{ color: '#F1F5F9', fontSize: 28, fontWeight: 700, lineHeight: 1 }}>{value ?? '—'}</div>
      {sub && <div style={{ color: '#4B5563', fontSize: 11, marginTop: 4 }}>{sub}</div>}
    </div>
  );
};

export default function WateringHole({ onNavigate }) {
  const isHQ = useIsHQ();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navFeed, setNavFeed] = useState([]);
  const [composing, setComposing] = useState(null); // null | 'ask' | 'announce'
  const [navText, setNavText] = useState('');
  const [navBusy, setNavBusy] = useState(false);

  const [mine, setMine] = useState({ team_group: null, efforts: [] });

  useEffect(() => {
    Promise.all([
      crmService.getDashboard().then(setData),
      crmService.getNavigatorFeed().then(setNavFeed).catch(() => setNavFeed([])),
      crmService.getMyEfforts().then(setMine).catch(() => setMine({ team_group: null, efforts: [] })),
    ])
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // The viewer's upcoming milestones across their assigned efforts
  const myMilestones = (mine.efforts || [])
    .flatMap(p => (p.milestones || []).filter(m => !m.done).map(m => ({ ...m, project: p.name, stage: p.stage })))
    .sort((a, b) => (a.date || '9999').localeCompare(b.date || '9999'));
  const ms30 = myMilestones.filter(m => m.date && (new Date(m.date) - Date.now()) < 30 * 86400000);
  const msOverdue = myMilestones.filter(m => m.date && new Date(m.date) < Date.now());
  const hasEfforts = (mine.efforts || []).length > 0;

  const submitNavigator = async () => {
    if (!navText.trim()) return;
    setNavBusy(true);
    try {
      if (composing === 'announce') {
        await crmService.postNavigatorAnnouncement(navText.trim());
        const feed = await crmService.getNavigatorFeed();
        setNavFeed(feed);
      } else {
        await crmService.submitNavigatorFeedback(navText.trim());
        alert('Sent to the Lead Navigator — reviewed by Headquarters, then posted here anonymously with an answer.');
      }
      setNavText(''); setComposing(null);
    } catch (e) { alert(e.message); } finally { setNavBusy(false); }
  };

  const announcements = navFeed.filter(q => q.kind === 'announcement').slice(0, 3);
  const questions = navFeed.filter(q => q.kind !== 'announcement').slice(0, 5);

  if (loading) {
    return <div style={{ color: '#94A3B8', textAlign: 'center', padding: 40 }}>Filling the watering hole…</div>;
  }
  if (!data) {
    return <div style={{ color: '#94A3B8', textAlign: 'center', padding: 40 }}>Could not load dashboard.</div>;
  }

  const totalPipeline = Object.entries(data.pipeline || {})
    .filter(([stage]) => !['won', 'lost'].includes(stage))
    .reduce((sum, [, d]) => sum + (d.value || 0), 0);
  const dealCount = Object.entries(data.pipeline || {})
    .filter(([stage]) => !['won', 'lost'].includes(stage))
    .reduce((sum, [, d]) => sum + (d.count || 0), 0);

  const overdue = data.overdue_actions || [];
  const upcoming = data.upcoming_actions || [];
  const cov = data.coverage || { covered: 0, white_space: 0, total: 12, with_pipeline: 0 };
  const fw = data.funding_watch || { watching: 0, added_7d: 0, latest: [] };
  const wins = data.recent_wins || { count: 0, value: 0 };
  const land = data.landscape || { contacts_mapped: 0, opps_with_customer: 0 };

  return (
    <div style={{ padding: 'clamp(12px, 4vw, 20px)', maxWidth: 1280, margin: '0 auto' }}>
      {/* Overdue actions alert banner */}
      {overdue.length > 0 && (
        <div style={{
          marginBottom: 16,
          padding: '12px 16px',
          backgroundColor: '#EF444418',
          border: '1px solid #EF4444',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#EF444422';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#EF444418';
        }}
        onClick={() => onNavigate?.('contacts')}
        >
          <span style={{ fontSize: 20 }}>⚠️</span>
          <div>
            <div style={{ color: '#EF4444', fontWeight: 600, fontSize: 14 }}>
              {overdue.length} overdue action{overdue.length === 1 ? '' : 's'}
            </div>
            <div style={{ color: '#94A3B8', fontSize: 12, marginTop: 2 }}>
              Click to review and reschedule
            </div>
          </div>
          <span style={{ flex: 1 }} />
          <div style={{ color: '#EF4444', fontWeight: 600, fontSize: 14 }}>→</div>
        </div>
      )}

      {/* From the Lead Navigator — organization-wide announcements & policy Q&A, front and center */}
      <div style={{ ...card, marginBottom: 16, borderTop: '2px solid #c4a563' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: (announcements.length || questions.length || composing) ? 12 : 0, flexWrap: 'wrap' }}>
          <div>
            <h3 style={{ ...cardTitle, margin: 0 }}>From the Lead Navigator</h3>
            <div style={{ fontSize: 10, color: '#4B5563', marginTop: 2 }}>Organization-wide announcements · policy questions answered for everyone</div>
          </div>
          <span style={{ flex: 1 }} />
          {isHQ && (
            <button onClick={() => { setComposing(c => c === 'announce' ? null : 'announce'); setNavText(''); }} style={{
              background: '#c4a56318', border: '1px solid #c4a56344', color: '#c4a563',
              borderRadius: 4, padding: '5px 14px', cursor: 'pointer', fontSize: 11, fontWeight: 700,
            }}>
              {composing === 'announce' ? 'Close' : '📣 Post Announcement'}
            </button>
          )}
          <button onClick={() => { setComposing(c => c === 'ask' ? null : 'ask'); setNavText(''); }} style={{
            background: '#0D1F3C', border: '1px solid #22D3EE44', color: '#22D3EE',
            borderRadius: 4, padding: '5px 14px', cursor: 'pointer', fontSize: 11, fontWeight: 600,
          }}>
            {composing === 'ask' ? 'Close' : 'Talk to the Lead Navigator'}
          </button>
        </div>

        {composing && (
          <div style={{ marginBottom: 12 }}>
            <textarea
              value={navText} onChange={e => setNavText(e.target.value)}
              placeholder={composing === 'announce'
                ? 'Announcement to the whole organization — policy updates, milestones, direction. Posts immediately under the HQ banner.'
                : 'Questions, friction, policy concerns — anything. Submissions are anonymous, reviewed by Headquarters, and answered here for everyone.'}
              style={{ width: '100%', boxSizing: 'border-box', background: '#0A0F1C', border: '1px solid #1E2D45', color: '#F1F5F9', padding: '8px 10px', borderRadius: 6, fontSize: 12, height: 64, resize: 'vertical' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 6 }}>
              <button onClick={submitNavigator} disabled={navBusy || !navText.trim()} style={{
                padding: '5px 16px', background: composing === 'announce' ? '#c4a563' : '#22D3EE', color: '#0A0F1C', border: 'none',
                borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 700, opacity: navText.trim() ? 1 : 0.5,
              }}>
                {navBusy ? 'Sending…' : composing === 'announce' ? 'Post to Organization' : 'Send Anonymously'}
              </button>
            </div>
          </div>
        )}

        {announcements.map(a => (
          <div key={a.id} style={{ padding: '10px 12px', borderLeft: '3px solid #c4a563', background: '#c4a56308', borderRadius: 6, marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginBottom: 4 }}>
              <span style={{ fontSize: 9, color: '#c4a563', fontWeight: 700, letterSpacing: '0.08em', border: '1px solid #c4a56344', borderRadius: 8, padding: '1px 8px' }}>📣 ANNOUNCEMENT</span>
              <span style={{ fontSize: 10, color: '#4B5563' }}>{new Date(a.created_at).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}</span>
            </div>
            <div style={{ fontSize: 13, color: '#F1F5F9', lineHeight: 1.6 }}>{a.body}</div>
          </div>
        ))}

        {questions.length === 0 && announcements.length === 0 && !composing && (
          <div style={{ fontSize: 11, color: '#374151', marginTop: 8 }}>
            Nothing posted yet — {isHQ ? 'share the first announcement, or ' : ''}ask the first question. Submissions are anonymous and answered by Headquarters.
          </div>
        )}

        {questions.map(q => (
          <div key={q.id} style={{ padding: '8px 0', borderTop: '1px solid #0A0F1C' }}>
            <div style={{ fontSize: 12, color: '#94A3B8', fontStyle: 'italic', lineHeight: 1.6 }}>"{q.body}"</div>
            {q.answer ? (
              <div style={{ display: 'flex', gap: 8, marginTop: 6, alignItems: 'baseline' }}>
                <span style={{ fontSize: 9, color: '#c4a563', fontWeight: 700, flexShrink: 0, border: '1px solid #c4a56344', borderRadius: 8, padding: '1px 7px' }}>HQ</span>
                <span style={{ fontSize: 12, color: '#F1F5F9', lineHeight: 1.6 }}>{q.answer}</span>
              </div>
            ) : (
              <div style={{ fontSize: 10, color: '#4B5563', marginTop: 4 }}>Awaiting answer from Headquarters…</div>
            )}
          </div>
        ))}
      </div>

      {/* Stat row — tailored to the viewer: HQ command view, builder view, or general view */}
      <div style={{ display: 'grid', gridTemplateColumns: (isHQ || hasEfforts) ? 'repeat(4, 1fr)' : 'repeat(3, 1fr)', gap: 12, marginBottom: 16 }}>
        {!isHQ && hasEfforts ? (
          <>
            <StatCard
              label="My Efforts" value={mine.efforts.length}
              sub={(mine.efforts[0] && `lead effort: ${mine.efforts[0].name}`.slice(0, 44)) || 'assigned development efforts'}
              onClick={() => onNavigate?.('family')}
            />
            <StatCard
              label="Milestones (30d)" value={ms30.length}
              sub={msOverdue.length > 0 ? `${msOverdue.length} overdue` : myMilestones[0]?.date ? `next: ${new Date(myMilestones[0].date).toLocaleDateString()}` : 'none scheduled'}
              accent={msOverdue.length > 0 ? '#EF4444' : '#8B5CF6'}
              onClick={() => onNavigate?.('family')}
            />
          </>
        ) : (
          <StatCard
            label="Active Pipeline" value={formatM(totalPipeline)}
            sub={`${dealCount} pursuit${dealCount === 1 ? '' : 's'} pre-award${wins.count > 0 ? ` · ${wins.count} won (30d): ${formatM(wins.value)}` : ''}`}
            onClick={() => onNavigate?.('pipeline')}
          />
        )}
        <StatCard
          label="Actions Due" value={upcoming.length + overdue.length}
          sub={overdue.length > 0 ? `${overdue.length} overdue` : 'nothing overdue'}
          accent={overdue.length > 0 ? '#EF4444' : '#10B981'}
          onClick={() => onNavigate?.('contacts')}
        />
        <StatCard
          label="Technology Gap" value={`${cov.white_space} of ${cov.total}`}
          sub={`white-space verticals · ${cov.covered} covered, ${cov.with_pipeline} with pipeline`}
          accent="#c4a563"
          onClick={isHQ ? () => onNavigate?.('coverage') : undefined}
        />
        {isHQ && (
          <StatCard
            label="Deal Flow" value={fw.watching}
            sub={fw.added_7d > 0 ? `+${fw.added_7d} new this week` : 'companies watched'}
            onClick={() => onNavigate?.('growth')}
          />
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16, marginBottom: 16 }}>
        {/* Pipeline by stage */}
        <div style={card}>
          <h3 style={cardTitle}>Pipeline by Stage</h3>
          {Object.entries(data.pipeline || {}).map(([stage, d]) => (
            <div
              key={stage}
              onClick={() => onNavigate?.('pipeline')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
                padding: '6px 8px',
                borderRadius: 4,
                cursor: 'pointer',
                backgroundColor: 'rgba(34, 211, 238, 0)',
                transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(34, 211, 238, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(34, 211, 238, 0)';
              }}
            >
              <div style={{ width: 80, fontSize: 11, color: '#94A3B8', textTransform: 'capitalize' }}>{stage}</div>
              <div style={{ flex: 1, height: 6, background: '#0A0F1C', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: totalPipeline > 0 ? `${Math.min(100, (d.value / totalPipeline) * 100)}%` : '0%',
                  background: STAGE_COLORS[stage] || '#374151',
                }} />
              </div>
              <div style={{ width: 60, textAlign: 'right', fontSize: 11, color: '#94A3B8' }}>{formatM(d.value)}</div>
              <div style={{ width: 24, textAlign: 'right', fontSize: 11, color: '#4B5563' }}>{d.count}</div>
            </div>
          ))}
          {dealCount === 0 && (
            <div style={{ fontSize: 12, color: '#374151' }}>
              No opportunities yet — start the charge from the Pipeline tab.
            </div>
          )}
        </div>

        {/* Next actions */}
        <div style={card}>
          <h3 style={cardTitle}>Next Actions</h3>
          {[...overdue.map(a => ({ ...a, overdue: true })), ...upcoming].slice(0, 7).map(a => (
            <div key={`${a.overdue ? 'o' : 'u'}-${a.id}`} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '5px 0', borderBottom: '1px solid #0A0F1C' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: a.overdue ? '#EF4444' : '#22D3EE', flexShrink: 0, alignSelf: 'center' }} />
              <span style={{ fontSize: 12, color: '#F1F5F9', fontWeight: 500, whiteSpace: 'nowrap' }}>{a.first_name} {a.last_name}</span>
              <span style={{ fontSize: 11, color: '#6B7280', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.next_action}</span>
              <span style={{ fontSize: 10, color: a.overdue ? '#EF4444' : '#22D3EE', whiteSpace: 'nowrap' }}>
                {new Date(a.next_action_date).toLocaleDateString()}
              </span>
            </div>
          ))}
          {overdue.length + upcoming.length === 0 && (
            <div style={{ fontSize: 12, color: '#374151' }}>
              No follow-ups scheduled. Log interactions with next actions to keep the herd moving.
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16, marginBottom: 16 }}>
        {/* Deal radar (HQ only) · my milestones (assigned members) · coverage posture (everyone else) */}
        {isHQ ? (
          <div style={card}>
            <h3 style={cardTitle}>Deal Radar — Newest in Watchlist</h3>
            {(fw.latest || []).map(c => (
              <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '5px 0', borderBottom: '1px solid #0A0F1C' }}>
                <span style={{ fontSize: 12, color: '#F1F5F9', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.company_name}</span>
                <span style={{ fontSize: 10, color: '#6B7280' }}>{(c.funding_stage || '').replace(/_/g, ' ')}</span>
                <span style={{ fontSize: 11, color: '#22D3EE', width: 56, textAlign: 'right' }}>
                  {c.latest_round_usd_millions ? `$${parseFloat(c.latest_round_usd_millions).toFixed(1)}M` : '—'}
                </span>
              </div>
            ))}
            {(fw.latest || []).length === 0 && (
              <div style={{ fontSize: 12, color: '#374151' }}>Watchlist is empty — run a deal flow sync from the Matriarch tab.</div>
            )}
          </div>
        ) : hasEfforts ? (
          <div style={card}>
            <h3 style={cardTitle}>My Milestones</h3>
            {myMilestones.slice(0, 7).map((m, i) => {
              const late = m.date && new Date(m.date) < Date.now();
              return (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'baseline', padding: '5px 0', borderBottom: '1px solid #0A0F1C' }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: late ? '#EF4444' : '#8B5CF6', flexShrink: 0, alignSelf: 'center' }} />
                  <span style={{ fontSize: 12, color: '#F1F5F9', fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.label}</span>
                  <span style={{ fontSize: 10, color: '#6B7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{m.project}</span>
                  <span style={{ fontSize: 10, color: late ? '#EF4444' : '#22D3EE', whiteSpace: 'nowrap' }}>
                    {m.date ? new Date(m.date).toLocaleDateString() : '—'}
                  </span>
                </div>
              );
            })}
            {myMilestones.length === 0 && (
              <div style={{ fontSize: 12, color: '#374151' }}>
                No open milestones on your efforts — check The Family for your assignments.
              </div>
            )}
          </div>
        ) : (
          <div style={card}>
            <h3 style={cardTitle}>Coverage Posture</h3>
            <div style={{ fontSize: 12, color: '#94A3B8', lineHeight: 1.7 }}>
              {cov.covered} of {cov.total} verticals have subsidiary coverage and {cov.with_pipeline} carry pipeline.
              The remaining {cov.white_space} are white space — the customer's technology gap and your growth signal.
            </div>
          </div>
        )}

        {/* Awards intelligence widget */}
        <AwardsWidget />

        {/* Top contacts */}
        <div style={card}>
          <h3 style={cardTitle}>Highest Authority Contacts</h3>
          {(data.top_contacts || []).slice(0, 6).map(c => (
            <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', borderBottom: '1px solid #0A0F1C' }}>
              <div style={{
                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                background: '#0A0F1C', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 11, color: '#22D3EE', fontWeight: 700,
              }}>{c.authority_score}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#F1F5F9', fontSize: 12, fontWeight: 500 }}>
                  {c.rank_code || c.grade_code ? `${c.rank_code || c.grade_code} ` : ''}{c.last_name}, {c.first_name}
                </div>
                <div style={{ color: '#6B7280', fontSize: 10, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.title || ''}{c.org_acronym ? ` · ${c.org_acronym}` : ''}
                </div>
              </div>
              <div style={{ color: c.relationship_stage === 'champion' ? '#22D3EE' : c.relationship_stage === 'trusted' ? '#10B981' : '#374151', fontSize: 10 }}>
                {c.relationship_stage}
              </div>
            </div>
          ))}
          {(data.top_contacts || []).length === 0 && (
            <div style={{ fontSize: 12, color: '#374151' }}>
              No contacts yet — the herd starts with one. Add them in The Herd tab.
            </div>
          )}
        </div>
      </div>

      {/* Mission strip — landscape footprint */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', borderLeft: '3px solid #c4a563' }}>
        <img src="/elephant-icon.svg" alt="" style={{ width: 36, height: 36, opacity: 0.5 }} />
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ color: '#F1F5F9', fontSize: 13, fontWeight: 600, marginBottom: 2 }}>The Savanna — your map of the customer</div>
          <div style={{ color: '#6B7280', fontSize: 11 }}>
            {data.stats.organizations} organizations charted · {land.contacts_mapped} contact{land.contacts_mapped === 1 ? '' : 's'} mapped to offices · {land.opps_with_customer} pursuit{land.opps_with_customer === 1 ? '' : 's'} tied to a customer
          </div>
        </div>
        <button onClick={() => onNavigate?.('landscape')} style={{ background: 'none', border: '1px solid #c4a56344', color: '#c4a563', borderRadius: 4, padding: '6px 14px', cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
          Walk the Savanna →
        </button>
      </div>
    </div>
  );
}
